/**
 * Playback intent and the clocks that can disagree with it.
 *
 * None of the types in this file depend on the DOM. Android can freeze the page while
 * Web Audio keeps advancing, while WebKit has shipped the inverse: a context which says
 * `running` whose clock does not move. Keeping the decisions pure makes those two failure
 * modes reproducible without pretending a desktop browser can reproduce a locked phone.
 */

export type PlaybackIntent = 'playing' | 'user-paused' | 'ended';

export type IntentEvent =
  | 'sit-started'
  | 'user-pause'
  | 'user-play'
  | 'renderer-interrupted'
  | 'sit-ended';

export function nextPlaybackIntent(current: PlaybackIntent, event: IntentEvent): PlaybackIntent {
  if (event === 'sit-ended') return 'ended';
  if (event === 'sit-started') return 'playing';
  if (event === 'renderer-interrupted') return current;
  if (current === 'ended') return current;
  return event === 'user-pause' ? 'user-paused' : 'playing';
}

export class PlaybackIntentMachine {
  private value: PlaybackIntent = 'ended';

  constructor(private readonly changed: (intent: PlaybackIntent) => void = () => undefined) {}

  get intent(): PlaybackIntent { return this.value; }

  dispatch(event: IntentEvent): PlaybackIntent {
    const next = nextPlaybackIntent(this.value, event);
    if (next !== this.value) {
      this.value = next;
      this.changed(next);
    }
    return this.value;
  }
}

export type RecoveryTrigger =
  | 'visibility-visible'
  | 'pageshow'
  | 'page-resume'
  | 'media-session-play'
  | 'context-state'
  | 'user-play';

export type RendererState = AudioContextState | 'interrupted';

export type RecoveryResult =
  | 'recovered'
  | 'timed-out'
  | 'rejected'
  | 'skipped-ended'
  | 'skipped-user-paused'
  | 'skipped-closed'
  | 'skipped-hidden';

export interface RecoveryAttempt {
  trigger: RecoveryTrigger;
  result: RecoveryResult;
  error?: string;
}

/**
 * A user pressing Resume always has to reach the renderer. Web Audio does that through
 * its guarded recovery path; the native engine has no RecoveryController, so it must be
 * told to play directly.
 */
export async function requestUserPlayback(
  play: () => Promise<void>,
  recovery: RecoveryController | null,
): Promise<void> {
  if (recovery) {
    await recovery.trigger('user-play');
  } else {
    await play();
  }
}

interface RecoveryDependencies {
  getIntent: () => PlaybackIntent;
  getState: () => RendererState;
  isVisible: () => boolean;
  resumeContext: () => Promise<void>;
  recoverRenderer: () => Promise<void>;
  onAttempt?: (attempt: RecoveryAttempt) => void;
  timeoutMs?: number;
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (timer: ReturnType<typeof setTimeout>) => void;
}

/**
 * One recovery attempt, shared by every trigger which arrives while it is pending.
 *
 * A WebKit `resume()` promise can stay pending indefinitely. Racing it is not enough if
 * work is chained to that promise: the late resolution would still mutate the renderer
 * after this attempt had failed. `recoverRenderer` is therefore called only by the side
 * which wins the race.
 */
export class RecoveryController {
  private pending: Promise<RecoveryAttempt> | null = null;
  private generation = 0;
  private readonly timeoutMs: number;
  private readonly setTimer: NonNullable<RecoveryDependencies['setTimer']>;
  private readonly clearTimer: NonNullable<RecoveryDependencies['clearTimer']>;

  constructor(private readonly deps: RecoveryDependencies) {
    this.timeoutMs = deps.timeoutMs ?? 4000;
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((timer) => clearTimeout(timer));
  }

  trigger(trigger: RecoveryTrigger): Promise<RecoveryAttempt> {
    if (this.pending) return this.pending;

    const skipped = this.guard(trigger);
    if (skipped) {
      const attempt = { trigger, result: skipped } satisfies RecoveryAttempt;
      this.deps.onAttempt?.(attempt);
      return Promise.resolve(attempt);
    }

    const attempt = this.run(trigger, this.generation);
    this.pending = attempt;
    void attempt.finally(() => {
      if (this.pending === attempt) this.pending = null;
    });
    return attempt;
  }

  cancel(): void {
    this.generation++;
    this.pending = null;
  }

  private guard(trigger: RecoveryTrigger): RecoveryResult | null {
    const intent = this.deps.getIntent();
    if (intent === 'ended') return 'skipped-ended';
    if (intent === 'user-paused') return 'skipped-user-paused';
    if (this.deps.getState() === 'closed') return 'skipped-closed';
    // A lock-screen Media Session play is itself a user request. Every automatic path
    // waits until visible; repeated resume attempts while hidden have caused stuck
    // promises in WebKit and cannot be made useful by spinning.
    if (!this.deps.isVisible() && trigger !== 'media-session-play') return 'skipped-hidden';
    return null;
  }

  private async run(trigger: RecoveryTrigger, generation: number): Promise<RecoveryAttempt> {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<'timed-out'>((resolve) => {
      timer = this.setTimer(() => resolve('timed-out'), this.timeoutMs);
    });
    const resume = Promise.resolve()
      .then(() => this.deps.resumeContext())
      .then(() => 'resumed' as const, (error: unknown) => ({ error }));
    const outcome = await Promise.race([resume, timeout]);
    if (timer !== null) this.clearTimer(timer);

    if (generation !== this.generation) {
      return { trigger, result: 'skipped-ended' };
    }

    if (outcome === 'timed-out') return this.finish({ trigger, result: 'timed-out' });
    if (typeof outcome === 'object') {
      return this.finish({
        trigger,
        result: 'rejected',
        error: outcome.error instanceof Error ? outcome.error.message : String(outcome.error),
      });
    }

    try {
      await this.deps.recoverRenderer();
      if (generation !== this.generation) return { trigger, result: 'skipped-ended' };
      return this.finish({ trigger, result: 'recovered' });
    } catch (error) {
      if (generation !== this.generation) return { trigger, result: 'skipped-ended' };
      return this.finish({
        trigger,
        result: 'rejected',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  private finish(attempt: RecoveryAttempt): RecoveryAttempt {
    this.deps.onAttempt?.(attempt);
    return attempt;
  }
}

export interface ClockSample {
  wallMs: number;
  perfMs: number;
  ctxSec: number;
  contextState: RendererState;
  intent: PlaybackIntent;
  /** Position derived only from the audio clock. */
  programSec: number;
  /** End of the continuous interval for which sources were actually attached. */
  scheduledToSec: number;
}

export interface ClockDrift {
  classification: 'running-stuck' | 'scheduler-thaw';
  wallDeltaMs: number;
  perfDeltaMs: number;
  ctxDeltaSec: number;
  programSec: number;
  scheduledToSec: number;
}

interface WatchdogOptions {
  minimumTickMs?: number;
  stuckAudioSec?: number;
  coverageToleranceSec?: number;
}

export class ClockProgressWatchdog {
  private previous: ClockSample | null = null;
  private active: ClockDrift['classification'] | null = null;
  private readonly minimumTickMs: number;
  private readonly stuckAudioSec: number;
  private readonly coverageToleranceSec: number;

  constructor(options: WatchdogOptions = {}) {
    this.minimumTickMs = options.minimumTickMs ?? 750;
    this.stuckAudioSec = options.stuckAudioSec ?? 0.05;
    this.coverageToleranceSec = options.coverageToleranceSec ?? 0.1;
  }

  reset(): void {
    this.previous = null;
    this.active = null;
  }

  tick(sample: ClockSample): ClockDrift | null {
    const previous = this.previous;
    this.previous = sample;
    if (!previous || sample.intent !== 'playing' || sample.contextState !== 'running') {
      this.active = null;
      return null;
    }

    const driftBase = {
      wallDeltaMs: sample.wallMs - previous.wallMs,
      perfDeltaMs: sample.perfMs - previous.perfMs,
      ctxDeltaSec: sample.ctxSec - previous.ctxSec,
      programSec: sample.programSec,
      scheduledToSec: sample.scheduledToSec,
    };
    let classification: ClockDrift['classification'] | null = null;
    if (sample.programSec > sample.scheduledToSec + this.coverageToleranceSec) {
      classification = 'scheduler-thaw';
    } else if (Math.max(driftBase.wallDeltaMs, driftBase.perfDeltaMs) >= this.minimumTickMs
        && driftBase.ctxDeltaSec <= this.stuckAudioSec) {
      classification = 'running-stuck';
    }

    if (!classification) {
      this.active = null;
      return null;
    }
    if (this.active === classification) return null;
    this.active = classification;
    return { classification, ...driftBase };
  }
}
